/**
 * The workspace's retention window, as a founder setting.
 *
 * It sits next to `DataModeCard` and follows it exactly: founder-only, server-
 * enforced, and the reader is told what the setting *does* rather than only what
 * it is. A member sees the window as a sentence, never as a control — the same
 * shape the data-mode card uses for a mode it may show but not change.
 *
 * The one thing this card must say out loud is the asymmetry, because it is not
 * what a settings field usually does: **shortening reaches backwards** into the
 * dossiers already stored, **lengthening does not**. A founder who lowers the
 * window to seven days for an architect is changing the promise on the dossier
 * that architect already handed over; a founder who raises it is not extending
 * anything already given. Leaving that to be discovered would make the control
 * look like it silently deleted files.
 *
 * **A shortening asks first.** Saying it next to the field was not enough: the
 * click makes stored dossiers due, and the next sweep then removes their PDFs,
 * their facts and every run and report of them, without waiting for anything
 * else. Everywhere else in this product a destructive act asks — a dossier
 * deletion opens an inline `role="alertdialog"` before the second press — and
 * this is the same question about the same loss, so it is the same panel: the
 * way out is the default focus, Escape closes it, and the destructive button is
 * reached on purpose. What it does *not* copy from the workspace-deletion panel
 * is the typed short name: the founder has already typed the number that does
 * the damage, and asking them to type it a second time would be ceremony rather
 * than deliberation. The confirmation lives in the client only — see
 * `decisions.md` for why the API does not grow a confirm token for a settings
 * field.
 *
 * The dialog states what happens to *this* workspace's data — how many dossiers
 * are re-dated and, separately and most legibly, how many become due right away
 * — computed by `retentionImpact` from the list the screen already loaded. When
 * that number is zero the dialog says so plainly instead of implying a loss that
 * will not happen.
 *
 * The bounds below are the server's (`app/models.py`), repeated here so the
 * input can refuse an obvious typo before the round trip. They are not the
 * enforcement: an out-of-range value that reaches the API comes back as a 422
 * with the reason, and that sentence is what the reader sees.
 */
import { useEffect, useRef, useState } from 'react'
import { Clock } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { isShortening, retentionImpact } from '@/features/workspace/retentionImpact'
import { useI18n } from '@/i18n'
import type { DossierRef, WorkspaceRef } from '@/api/types'

/** Mirrors `MIN_RETENTION_DAYS` / `MAX_RETENTION_DAYS` in `app/models.py`. */
export const MIN_RETENTION_DAYS = 1
export const MAX_RETENTION_DAYS = 365

export function RetentionWindowCard({
  workspace,
  canChange,
  onChanged,
  dossiers = null,
  now,
}: {
  workspace: WorkspaceRef
  canChange: boolean
  onChanged: () => void
  /**
   * The workspace's dossiers, for the confirmation's arithmetic. `null` means
   * "not loaded" — the dialog then says the count is unavailable rather than
   * reporting a reassuring zero it cannot stand behind.
   */
  dossiers?: readonly DossierRef[] | null
  /** Injectable clock; the dialog's "due immediately" is a comparison to now. */
  now?: Date
}) {
  const { t } = useI18n()
  const [days, setDays] = useState(String(workspace.retention_days))
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** The value waiting for a confirmation. `null` = no question open. */
  const [pending, setPending] = useState<number | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  /** A question is on screen: the form behind it is read-only until it is answered. */
  const asking = pending !== null

  // Opening the question moves focus into it, and onto «Abbrechen» — the way
  // out is the default, the shortening has to be reached deliberately. Same
  // rule as the dossier deletion panel, for the same reason.
  useEffect(() => {
    if (asking) panelRef.current?.querySelector('button')?.focus()
  }, [asking])

  const commit = async (next: number) => {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.setRetentionDays(workspace.id, next)
      setSaved(updated.retention_days)
      setPending(null)
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const save = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSaved(null)
    const next = Number(days)
    // Only a shortening reaches backwards. Lengthening and a no-op destroy
    // nothing, so they stay one click — a dialog there would be noise, and
    // noise is what teaches a reader to click through the real one.
    if (isShortening(workspace.retention_days, next)) {
      setPending(next)
      return
    }
    void commit(next)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.window.title')}</CardTitle>
        <CardDescription>
          {t('workspace.window.lead', { min: MIN_RETENTION_DAYS, max: MAX_RETENTION_DAYS })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-[15px] text-ink" data-testid="retention-window-current">
          <Icon icon={Clock} className="me-1.5 inline text-muted" />
          {t('workspace.retention', { days: workspace.retention_days })}
        </p>

        {canChange ? (
          <form className="flex flex-wrap items-end gap-3" onSubmit={save}>
            {/* `min-w-0` so the flex item may shrink at 320 CSS px (WCAG 2.2 §1.4.10). */}
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="retention-days" className="text-[13px] font-medium text-ink-2">
                {t('workspace.window.label')}
              </label>
              {/* Frozen while the question is open. The dialog quotes a number
                  and `commit(pending)` sends that number; a field still editable
                  underneath would let the reader change 7 to 3, press «Frist
                  verkürzen», and get the 7 the panel promised — the field and
                  the act would disagree about what was just done. The value is
                  editable again the moment the question is cancelled. */}
              <input
                id="retention-days"
                type="number"
                required
                min={MIN_RETENTION_DAYS}
                max={MAX_RETENTION_DAYS}
                step={1}
                value={days}
                disabled={asking}
                onChange={(event) => setDays(event.target.value)}
                className="h-10 w-[120px] max-w-full min-w-0 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink disabled:opacity-60"
              />
            </div>
            {/* Same reason: a second «Frist setzen» while the panel is up would
                re-aim the open question at a different number. */}
            <Button type="submit" disabled={busy || asking || days.trim() === ''}>
              {t('workspace.window.save')}
            </Button>
          </form>
        ) : null}

        {asking ? (
          <ShorteningConfirmation
            workspace={workspace}
            next={pending}
            dossiers={dossiers}
            now={now}
            busy={busy}
            error={error}
            panelRef={panelRef}
            onCancel={() => setPending(null)}
            onConfirm={() => void commit(pending)}
          />
        ) : null}

        {/* The sentence the control exists to be honest about. Shown to everyone
            who can see the card, not only to the founder about to click. */}
        <p className="text-[12px] text-muted" data-testid="retention-window-asymmetry">
          {t('workspace.window.asymmetry')}
        </p>
        <p className="text-[12px] text-muted">{t('workspace.window.backups')}</p>

        {/* While the question is open the refusal belongs inside it, next to the
            button that produced it — not down here, out of the reader's view. */}
        {error && !asking ? <ErrorNote message={error} /> : null}
        {saved !== null ? (
          <p role="status" className="text-[13px] text-ink" data-testid="retention-window-saved">
            {t('workspace.window.saved', { days: saved })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * "You are about to shorten the window — here is what that does to your data."
 *
 * The panel's shape is the dossier deletion's (`DeleteDossierButton`): an inline
 * `role="alertdialog"` rather than a modal — no focus trap to get wrong, Escape
 * closes it, both buttons in the normal tab order, and the destructive one never
 * the first focus target.
 *
 * The order of the sentences is the order the founder needs them in: what is
 * changing, **how many dossiers go now**, how many are merely re-dated, and only
 * then what "go" means. The due count is the largest text in the panel because
 * it is the only number that describes a loss.
 */
function ShorteningConfirmation({
  workspace,
  next,
  dossiers,
  now,
  busy,
  error,
  panelRef,
  onCancel,
  onConfirm,
}: {
  workspace: WorkspaceRef
  next: number
  dossiers: readonly DossierRef[] | null
  now?: Date
  busy: boolean
  error: string | null
  panelRef: React.RefObject<HTMLDivElement | null>
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useI18n()
  const impact = dossiers === null ? null : retentionImpact(dossiers, next, now ?? new Date())
  const destructive = impact !== null && impact.dueImmediately > 0

  return (
    <div
      role="alertdialog"
      aria-label={t('workspace.window.confirm.title')}
      aria-describedby="retention-window-confirm-what"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel()
      }}
      ref={panelRef}
      className="flex w-full flex-col gap-3 rounded-sm border border-border-strong bg-surface px-4 py-3"
      data-testid="retention-window-confirm"
    >
      <p className="text-[15px] font-medium text-ink">{t('workspace.window.confirm.title')}</p>
      <p className="text-[13px] text-ink-2" data-testid="retention-window-confirm-change">
        {t('workspace.window.confirm.change', { from: workspace.retention_days, to: next })}
      </p>

      <div id="retention-window-confirm-what" className="flex flex-col gap-2">
        {impact === null ? (
          <p className="text-[13px] text-muted" data-testid="retention-window-confirm-unknown">
            {t('workspace.window.confirm.unknown')}
          </p>
        ) : (
          <>
            {/* The number that matters, and the only one set in display size.
                At zero it is a plain sentence in the ordinary colour: nothing is
                lost, and a red heading would say otherwise. */}
            <p
              className={
                destructive
                  ? 'font-display text-[21px] leading-snug font-semibold text-signal-red-text'
                  : 'text-[15px] text-ink'
              }
              data-testid="retention-window-confirm-due"
            >
              {destructive
                ? t('workspace.window.confirm.due', {
                    count: impact.dueImmediately,
                    total: impact.total,
                  })
                : t('workspace.window.confirm.dueNone')}
            </p>
            <p className="text-[13px] text-muted" data-testid="retention-window-confirm-redated">
              {t('workspace.window.confirm.redated', {
                count: impact.redated,
                total: impact.total,
              })}
            </p>
          </>
        )}
        {/* What "due" costs, and what survives it — only where something is
            actually about to go. */}
        {destructive || impact === null ? (
          <>
            <p className="text-[13px] text-muted">{t('workspace.window.confirm.what')}</p>
            <p className="text-[13px] font-medium text-signal-red-text">
              {t('delete.irreversible')}
            </p>
            <p className="text-[13px] text-muted">{t('delete.keeps')}</p>
          </>
        ) : null}
      </div>

      {error ? <ErrorNote message={error} /> : null}

      <div className="flex flex-wrap gap-3">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={busy}>
          {t('delete.cancel')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={onConfirm}
          disabled={busy}
          data-testid="retention-window-confirmed"
          className={
            destructive || impact === null
              ? 'bg-signal-red border-signal-red hover:bg-signal-red-text hover:border-signal-red-text'
              : undefined
          }
        >
          {busy ? t('workspace.window.confirm.busy') : t('workspace.window.confirm.cta')}
        </Button>
      </div>
    </div>
  )
}
